import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import { UserFormComponent } from '../user-form/user-form.component';
import { UserService } from '../../../services/user.service';
import { CreateUserRequest } from '../../../models/api/createUserRequest';
import { UserMappingService } from '../../../services/user-mapping.service';
import { forkJoin, switchMap, of } from 'rxjs';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxSpinnerService } from 'ngx-spinner';

@Component({
  selector: 'app-user-create',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, UserFormComponent],
  templateUrl: './user-create.component.html',
  styleUrls: ['./user-create.component.scss']
})
export class UserCreateComponent {
  form: FormGroup;

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private userMappingService: UserMappingService,
    private router: Router,
    private toast: HotToastService,
    private spinner: NgxSpinnerService
  ) {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', Validators.required],
      fullName: ['', Validators.required],
      role: ['', Validators.required],
      category: [''],
      selectedCountries: [[]],
      selectedTabs: [[]],
      readonlyTabs: [[]],
      selectedMeasurements: [[]],
      selectedSections: [[]]
    }, { validators: this.passwordMatchValidator });
  }

  passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.get('password')?.value;
    const confirmPassword = control.get('confirmPassword')?.value;

    if (password !== confirmPassword) {
      control.get('confirmPassword')?.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    }

    return null;
  }

  onSubmit() {
    if (this.form.valid) {
      this.spinner.show();

      const createRequest: CreateUserRequest = {
        email: this.form.value.email,
        password: this.form.value.password,
        fullName: this.form.value.fullName,
        role: this.userMappingService.mapRoleToEnum(this.form.value.role),
        category: this.userMappingService.mapCategoryToEnum(this.form.value.category),
        companyAccess: this.userMappingService.mapCompanyAccessToEnums(this.form.value.selectedTabs || []),
        companyAccessReadOnly: this.userMappingService.mapTabNamesToEnums(this.form.value.readonlyTabs || []),
        accessToYears: (this.form.value.selectedMeasurements || []).map((year: string) => parseInt(year, 10))
      };

      this.userService.createUser(createRequest).pipe(
        switchMap((createdUser) => {
          const userId = createdUser.id;
          const updateRequests = [];

          if (this.form.value.selectedCountries?.length > 0) {
            updateRequests.push(
              this.userService.updateCountryAccess(userId, {
                countryIds: this.form.value.selectedCountries
              })
            );
          }

          if (this.form.value.selectedSections?.length > 0) {
            updateRequests.push(
              this.userService.updateSectionAccess(userId, {
                sectionAccess: this.form.value.selectedSections
              })
            );
          }

          if (updateRequests.length > 0) {
            return forkJoin(updateRequests);
          }

          return of(createdUser);
        })
      ).subscribe({
        next: () => {
          this.spinner.hide();
          this.toast.success('User created successfully', {
            iconTheme: {
              primary: 'var(--mat-sys-primary)',
              secondary: '#FFF',
            }
          });
          this.router.navigate(['/users']);
        },
        error: (error: Error) => {
          this.spinner.hide();
          this.toast.error('Error creating user: ' + error.message, {
            iconTheme: {
              primary: 'var(--mat-sys-error)',
              secondary: '#FFF',
            }
          });
          console.error('Error creating user:', error);
        }
      });
    }
  }

  onCancel() {
    this.router.navigate(['/users']);
  }
}
